<script setup lang="ts">
import type { WalletType } from '~/composables/usePlayWithBonus';

type Props = {
  gameDetails?: any;
  isGameModalOpen?: boolean;
  gameSlug: string;
  gameDemoMode: string;
  statistics: GameStatisticsByPeriod | null;
};

const { gameDetails, isGameModalOpen, gameSlug, gameDemoMode } =
  defineProps<Props>();

type Emits = {
  'full-screen-off': [];
};

const emit = defineEmits<Emits>();

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { ApiService } = useApiService();
const { isMobile } = useLayout();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();

const gameIframeElement = ref<HTMLIFrameElement | null>(null);

const { normalizeThumbnail } = useCasino();

const thumbnailPath = computed<string>(() => {
  return `${normalizeThumbnail(gameDetails.image, true)}/public`;
});

const route = useRoute('games-provider-game');

const providerNameFromUrl = computed<string>(() => {
  return route.params.provider;
});

const isLiveG24Provider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'liveg24';
});

const isPGSoftProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'pgsoft';
});

const isAltenteProvider = computed<boolean>(() => {
  return (
    providerNameFromUrl.value === 'altente' ||
    providerNameFromUrl.value === 'alg'
  );
});

const isAmusnetProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'amusnet';
});

const isEvolutionProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'evolution';
});

const isAviatrixProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'aviatrix';
});

const is7MojosProvider = computed<boolean>(() => {
  return providerNameFromUrl.value === 'sevenmojos';
});

const isGoTechProvider = computed<boolean>(() => {
  return (
    !!gameIframeDetails.value &&
    !!gameIframeDetails.value.url &&
    !!gameIframeDetails.value.url.includes('gotech')
  );
});

const iframePermissions = computed(() => {
  if (isAviatrixProvider.value) {
    return 'clipboard-write';
  }

  if (isAltenteProvider.value) {
    return 'autoplay; fullscreen';
  }

  if (isAmusnetProvider.value) {
    return 'autoplay; fullscreen; screen-wake-lock';
  }

  if (is7MojosProvider.value) {
    return 'autoplay; fullscreen';
  }

  return '';
});

function requestFullScreen() {
  if (!isMobile.value) {
    return;
  }

  const iframe = gameIframeElement.value;

  if (!iframe) {
    return;
  }

  if (iframe.requestFullscreen) {
    iframe.requestFullscreen().catch((err) => {
      console.error(
        `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
      );
    });
    // @ts-ignore
  } else if (iframe.webkitRequestFullScreen) {
    // @ts-ignore
    iframe.webkitRequestFullScreen();
    // @ts-ignore
  } else if (iframe.msRequestFullscreen) {
    // @ts-ignore
    iframe.msRequestFullscreen();
  }
}

const { goToLocaleRoute } = useRoutes();

async function goToProviderGamesPage() {
  await goToLocaleRoute({
    name: 'games-providers-provider',
    params: {
      provider: providerNameFromUrl.value,
    },
  });
}

function listenLiveG24({ data }: MessageEvent) {
  if (data === 'closeGame') {
    return goToProviderGamesPage();
  }
}

function listenAmusnet({ data }: MessageEvent) {
  if (!data) {
    return;
  }

  if (data.command === 'com.egt-bg.exit') {
    return goToProviderGamesPage();
  }

  if (data.command === 'com.egt-bg.reload') {
    location.reload();
  }
}

function onMessagesListener(message: MessageEvent) {
  if (isLiveG24Provider.value) {
    listenLiveG24(message);
  }

  if (isAmusnetProvider.value) {
    listenAmusnet(message);
  }
}

function onIframeLoad() {
  if (isAltenteProvider.value) {
    requestFullScreen();
  }
}

interface GameIframeDetails {
  url?: string;
  launchOptions?: any;
  isLoading: boolean;
  isFetching: boolean;
  hasError: boolean;
  errorMessage: string;
}

const gameIframeDetails = ref<GameIframeDetails>({
  isLoading: true,
  isFetching: false,
  hasError: false,
  errorMessage: '',
});

function resetGameIframeDetails() {
  gameIframeDetails.value.url = '';
  gameIframeDetails.value.launchOptions = null;
  gameIframeDetails.value.hasError = false;
  gameIframeDetails.value.isLoading = true;
  gameIframeDetails.value.isFetching = false;
  gameIframeDetails.value.errorMessage = '';
}

function setGameIframeError(message: string) {
  gameIframeDetails.value.hasError = true;
  gameIframeDetails.value.errorMessage = message;
  gameIframeDetails.value.isLoading = false;
  gameIframeDetails.value.isFetching = false;
}

const iframeBinds = computed<Record<string, any>>(() => {
  const binds: Record<string, any> = {
    id: 'gameIframe',
    allow: iframePermissions.value,
    frameborder: '0',
    height: '100%',
    title: `${gameDetails.name} Iframe`,
    width: '100%',
  };

  if (isPGSoftProvider.value && !isGoTechProvider.value) {
    binds.srcdoc = gameIframeDetails.value.url;
  } else {
    binds.src = gameIframeDetails.value.url;
  }

  if (isAltenteProvider.value || is7MojosProvider.value) {
    binds.allowfullscreen = true;
  }

  return binds;
});

// Jogar com Bonus — a carteira e escolhida UMA vez, antes do launch, e viaja
// no `?wallet=` da rota. Ficar na URL (e nao em estado volatil) e o que faz o
// vinculo sobreviver a reload/deep-link: sem isso, um F5 remontaria a sessao
// sem wallet_type e o core cairia no fallback legado sem o usuario saber.
const {
  isPlayWithBonusEnabled,
  walletChoiceVariant,
  resolveWalletType,
  ensureWalletLoaded,
  ensureRolloverLoaded,
  walletBonusFormatted,
  rollover,
  bonusExpiry,
  WALLET_TYPE,
  WALLET_TYPE_QUERY,
  WALLET_TYPE_PARAM,
} = usePlayWithBonus();

// Elegibilidade vem do `bonus_eligible` que o backend manda no proprio jogo —
// aqui pelo detalhe (`/games?slug=`), que e o que a pagina carrega. Enquanto o
// detalhe estiver carregando o campo e undefined e o gate nao monta; quando
// chega, o computed reavalia sozinho.
const bonusEligibilityTarget = computed(() => {
  return (gameDetails || null) as Game | null;
});

// `resolveWalletType` (e nao um parse cru) porque o `?wallet=` e entrada do
// usuario: link salvo, colado ou compartilhado pode pedir bonus num jogo nao
// elegivel ou depois do saldo zerar. Nesses casos cai em `wallet`, igual o
// core faz no passo 3 do launch — nunca manda `bonus` que vai ser
// recusado do outro lado.
const selectedWalletType = computed<WalletType | null>(() => {
  return resolveWalletType(
    route.query[WALLET_TYPE_QUERY],
    bonusEligibilityTarget.value,
  );
});

/**
 * Carteira que vai no launch. Bonus SO com escolha explicita no botao verde
 * (`?wallet=bonus`); qualquer outro caminho — jogo aberto direto,
 * deep-link, reload, botao "JOGAR" — manda `wallet` de forma explicita.
 *
 * Nao e o mesmo que `selectedWalletType`: aquele responde "o jogador escolheu?"
 * (e por isso pode ser null, o que abre o gate); este responde "com o que a
 * sessao vai rodar?", e nunca e null.
 *
 * Omitir o parametro NAO seria equivalente: sem `wallet_type` o core cai na
 * preferencia salva do jogador (`last_wallet_type`, que repete a ultima
 * escolha) e, na falta dela, no fallback legado do Observer — que e
 * `bonusWallet ?? wallet`, ou seja, PREFERE bonus. Mandar `wallet` explicito e
 * o que garante que so o botao verde gasta bonus.
 */
const launchWalletType = computed<WalletType>(() => {
  return selectedWalletType.value || WALLET_TYPE.REAL;
});

// So existe gate quando ha escolha real a fazer (jogo elegivel + saldo bonus).
// Jogo sem bonus segue exatamente como hoje, sem tela extra no caminho.
const needsWalletChoice = computed<boolean>(() => {
  return (
    isPlayWithBonusEnabled.value &&
    isAuthenticated.value &&
    gameDemoMode !== 'demo' &&
    !selectedWalletType.value &&
    walletChoiceVariant(bonusEligibilityTarget.value) === 'double'
  );
});

const router = useRouter();

function onWalletChosen(walletType: WalletType) {
  // `replace` pra escolha nao virar um passo no historico — o voltar do
  // usuario tem que sair do jogo, nao reabrir o gate.
  router.replace({
    query: { ...route.query, [WALLET_TYPE_QUERY]: walletType },
  });
}

const isDefaultIframeActive = computed(() => {
  return (
    !gameIframeDetails.value.launchOptions &&
    gameIframeDetails.value.url &&
    !gameIframeDetails.value.hasError
  );
});

const isAnyDataLoading = computed(() => {
  return gameDetails.isLoading || gameIframeDetails.value.isLoading;
});

// O cartao de pre-jogo serve dois estados: deslogado (erro de auth) e escolha
// de carteira. Mesma marcacao, so as acoes mudam — e o que mantem as duas
// telas padronizadas sem depender de ninguem lembrar de replicar estilo.
const showPreviewCard = computed<boolean>(() => {
  if (isAnyDataLoading.value) return false;

  return (
    needsWalletChoice.value ||
    (gameIframeDetails.value.hasError && !isAuthenticated.value)
  );
});

const showGenericError = computed<boolean>(() => {
  return (
    gameIframeDetails.value.hasError &&
    !isAnyDataLoading.value &&
    !showPreviewCard.value
  );
});

async function loadSoftswissGame() {
  if (!gameIframeDetails.value.launchOptions) {
    return;
  }

  const { game_url, mobile_url, desktop_url } =
    gameIframeDetails.value.launchOptions;

  const gameUrl = game_url || (isMobile.value ? mobile_url : desktop_url);

  if (!gameUrl) {
    return;
  }

  const GAME_WRAPPER_ID = 'game-wrapper';

  await sleep(200);

  // Garantimos que o iframe não seja duplicado
  const softSwissGameWrapper = document.getElementById(GAME_WRAPPER_ID);

  if (softSwissGameWrapper) {
    softSwissGameWrapper.innerHTML = '';
  }

  const gameLaunchOptions: any = { target_element: GAME_WRAPPER_ID };

  gameLaunchOptions.launch_options = {
    game_url: gameUrl,
    strategy: 'iframe',
  };

  const onSuccess = (success: any) => {
    console.log('✅ onSuccessLaunch: ', success);
  };

  const onError = (error: any) => {
    console.error('❌ onErrorLaunch: ', error);
  };

  await window.sg.launch(gameLaunchOptions, onSuccess, onError);
}

async function loadGameIframe(source: string) {
  if (!gameSlug) {
    return;
  }

  if (gameIframeDetails.value.isFetching) {
    return;
  }

  resetGameIframeDetails();

  // A decisao depende do saldo de bonus, entao a carteira precisa estar
  // resolvida ANTES de decidir — senao o launch sai na frente do fetch e o
  // gate nunca aparece. Marca isFetching antes do await pra que o guard acima
  // continue valendo durante a espera.
  if (isPlayWithBonusEnabled.value) {
    gameIframeDetails.value.isFetching = true;

    await ensureWalletLoaded();

    // Sem await: a regra de rollover e so exibicao, e o `summary` e reativo —
    // aparece sozinho quando a resposta chega. Esperar por ela atrasaria o
    // launch e o gate por um dado que nao decide nada.
    ensureRolloverLoaded();

    gameIframeDetails.value.isFetching = false;
  }

  // Sem carteira escolhida ainda: nao ha launch a fazer. Zera o loading pra
  // liberar o gate — o watch em selectedWalletType redispara quando escolher.
  if (needsWalletChoice.value) {
    gameIframeDetails.value.isLoading = false;

    return;
  }

  if (!isAuthenticated.value) {
    gameIframeDetails.value.url = '';
    gameIframeDetails.value.launchOptions = null;
    setGameIframeError(t('casino.game.login_error'));

    return;
  }

  const { isValidationModuleEnabled } = useModulesValidation();

  if (isValidationModuleEnabled('casino')) {
    await $containment.fetchCurrentPending('casino');

    if ($containment.hasPendencyOn('casino')) {
      const { $localePath } = useNuxtApp();
      const localePath = $localePath as ((p: string) => string) | undefined;

      $containment.closeVerificationModal();

      return await navigateTo(localePath ? localePath('/') : '/');
    }
  }

  const useDemo: number = gameDemoMode === 'demo' ? 1 : 0;
  const platform = isMobile.value ? 'MOBILE' : 'WEB';

  gameIframeDetails.value.isLoading = true;
  gameIframeDetails.value.isFetching = true;

  const params = new URLSearchParams({
    slug: gameSlug,
    platform,
    use_demo: String(useDemo),
    source,
    tab_id: getTabId(),
    mounted_id: getMountId(),
  });

  // Sempre explicito com a flag ligada: `bonus` so pelo botao verde, `cash` em
  // todo o resto. A spec diz que omitir equivale a `cash`, mas o doc de
  // arquitetura descreve fallback legado `bonusWallet ?? wallet` (prefere
  // bonus): mandar sempre custa nada e fecha a duvida. Com a flag desligada nao
  // mandamos nada e o fluxo atual segue intacto. Demo nao movimenta saldo.
  if (isPlayWithBonusEnabled.value && gameDemoMode !== 'demo') {
    params.set(WALLET_TYPE_PARAM, launchWalletType.value);
  }

  await ApiService.get(`/start-game-v2?${params}`)
    .then(async ({ data }) => {
      if (data.launchOptions) {
        if (data.launchOptions.launch_options) {
          gameIframeDetails.value.launchOptions =
            data.launchOptions.launch_options;
        } else {
          gameIframeDetails.value.launchOptions = data.launchOptions;
        }
      } else if (data.gameURL) {
        gameIframeDetails.value.url = data.gameURL;
      } else if (data.url) {
        gameIframeDetails.value.url = data.url;
      } else {
        setGameIframeError(t(`error.game_not_found`));
      }

      await loadSoftswissGame();
    })
    .catch((err) => {
      if (err.message && err.message.includes('401')) {
        setGameIframeError(
          err.message || err.description || t('casino.game.login_error'),
        );
      } else {
        setGameIframeError(
          err.message || err.description || t(`error.game_not_found`),
        );
      }
    })
    .finally(() => {
      gameIframeDetails.value.isLoading = false;
      gameIframeDetails.value.isFetching = false;
    });
}

onMounted(() => {
  window.addEventListener('message', onMessagesListener, { passive: true });
});

onBeforeRouteLeave(() => {
  window.removeEventListener('message', onMessagesListener);
});

watch(
  () => isAuthenticated.value,
  async () => {
    await loadGameIframe('watchIsAuthenticated');
  },
  {
    immediate: true,
  },
);

watch(
  () => selectedWalletType.value,
  async (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      await loadGameIframe('watchWalletType');
    }
  },
);

watch(
  () => gameDemoMode,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      setTimeout(async () => {
        await loadGameIframe('watchGameDemoMode');
      }, 300);
    }
  },
);
</script>

<template>
  <section
    :key="isEvolutionProvider ? 0 : isMobile ? 1 : 0"
    :class="[
      $style.frameGameSection,
      { [$style.isFullScreen]: isGameModalOpen },
    ]"
  >
    <CasinoGamesBar @full-screen-off="emit('full-screen-off')" />

    <div
      v-if="isAnyDataLoading"
      :class="$style.loadingSection"
    >
      <BaseLoading
        class="!bg-transparent !absolute"
        :show="true"
      />
    </div>

    <div
      v-if="showPreviewCard || showGenericError"
      :class="$style.errorSection"
    >
      <!-- Mesmo cartao de pre-jogo em dois estados: deslogado (botao unico,
           abre login) e escolha de carteira do Jogar com Bonus (dois botoes).
           So as acoes mudam — o resto e identico por construcao. -->
      <div
        v-if="showPreviewCard"
        :class="$style.authError"
      >
        <div :class="$style.previewStack">
          <div :class="$style.previewThumbWrapper">
            <div :class="$style.previewThumb">
              <CasinoMediaApostatudo
                v-if="gameDetails.image"
                :game="gameDetails"
                no-link
              />

              <NuxtIcon
                v-else
                class="text-4xl"
                name="exclamation"
              />
            </div>
          </div>

          <div :class="$style.previewCard">
            <header :class="$style.previewTopbar">
              <CasinoGameActionsMenu
                :game="gameDetails"
                share-only
              />
            </header>

            <div :class="$style.previewTitleBlock">
              <h2 :class="$style.previewName">{{ gameDetails.name }}</h2>
              <p :class="$style.previewProvider">
                {{
                  typeof gameDetails.provider === 'string'
                    ? gameDetails.provider
                    : gameDetails.provider?.name || ''
                }}
              </p>
            </div>

            <button
              :class="$style.previewPlayBtn"
              type="button"
              @click="
                needsWalletChoice
                  ? onWalletChosen(WALLET_TYPE.REAL)
                  : openLogin()
              "
            >
              <svg
                :class="$style.previewPlayIcon"
                fill="none"
                height="13"
                viewBox="0 0 11 13"
                width="11"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 6.5L0 0V13L11 6.5Z"
                  fill="currentColor"
                />
              </svg>
              JOGAR
            </button>

            <template v-if="needsWalletChoice">
              <button
                :class="[$style.previewPlayBtn, $style.previewBonusBtn]"
                type="button"
                @click="onWalletChosen(WALLET_TYPE.BONUS)"
              >
                <svg
                  :class="$style.previewBonusIcon"
                  fill="none"
                  height="14"
                  viewBox="0 0 14 14"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 6.5h12v6.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5V6.5ZM.5 4h13a.5.5 0 0 1 .5.5V6a.5.5 0 0 1-.5.5H.5A.5.5 0 0 1 0 6V4.5A.5.5 0 0 1 .5 4ZM7 4v9.5M7 4S5.8 1 4.2 1a1.7 1.7 0 0 0 0 3.4M7 4s1.2-3 2.8-3a1.7 1.7 0 0 1 0 3.4"
                    stroke="currentColor"
                    stroke-linejoin="round"
                    stroke-width="1.2"
                  />
                </svg>
                JOGAR COM BÔNUS
              </button>

              <!-- Saldo + regra de rollover sao obrigatorios pelo PRD
                   (RF-02/RF-03): o jogador tem que saber quanto tem e o que
                   precisa cumprir ANTES de comecar a partida com bonus. -->
              <p :class="$style.previewBonusBalance">
                Saldo bônus disponível:
                <strong>{{ walletBonusFormatted }}</strong>
              </p>

              <p
                v-if="rollover.summary"
                :class="$style.previewBonusRollover"
              >
                {{ rollover.summary }}
              </p>

              <p
                v-if="bonusExpiry.label"
                :class="$style.previewBonusRollover"
              >
                Válido até {{ bonusExpiry.label }}
              </p>
            </template>

            <CasinoGameTagChips
              v-if="gameDetails.tags?.length"
              :class="$style.previewChips"
              :reset-key="gameDetails.id"
              :tags="gameDetails.tags"
            />
          </div>
        </div>
      </div>

      <div
        v-else
        :class="$style.genericError"
      >
        <NuxtIcon
          class="text-4xl"
          name="exclamation"
        />

        <span>{{ gameIframeDetails.errorMessage }}</span>
      </div>
    </div>

    <div
      id="game-wrapper"
      :class="$style.gameWrapper"
    >
      <template v-if="isDefaultIframeActive">
        <iframe
          ref="gameIframeElement"
          v-bind="iframeBinds"
          @load.once="onIframeLoad"
        ></iframe>
      </template>
    </div>

    <div
      v-if="gameIframeDetails.hasError && !gameIframeDetails.isLoading"
      :class="$style.frameBg"
      :style="`background-image:url(${thumbnailPath})`"
    />
  </section>
</template>

<style module lang="postcss">
.frameGameSection {
  @apply relative flex flex-col w-full h-[44vh] lg:h-[70vh] bg-black border border-solid border-white/10 rounded-t-lg overflow-hidden;

  @media (max-width: 767px) {
    &:not(.isFullScreen) {
      @apply hidden;
    }
  }

  iframe {
    @apply w-full h-full relative z-10 overflow-hidden;
  }

  .loadingSection {
    @apply w-full h-full text-center flex items-center justify-center absolute z-10;

    svg {
      @apply w-12 h-12 text-primary;
    }
  }

  .errorSection {
    @apply absolute z-20 w-full h-full flex flex-col items-center justify-center gap-2.5 text-center font-bold text-sm md:text-base;

    svg {
      @apply w-2 h-2 text-3xl;
    }

    .authError {
      @apply flex flex-col items-center justify-center gap-6 text-white;

      .authErrorImage {
        @apply rounded-lg border-4 border-solid border-white/10 shadow-lg shadow-black/20 overflow-hidden;
        width: 192px;
        height: 224px;
        flex-shrink: 0;
      }

      .previewStack {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 320px;
        max-width: calc(100% - 32px);
      }

      .previewThumbWrapper {
        position: relative;
        z-index: 11;
        display: flex;
        justify-content: center;
        margin-bottom: -50px;
        pointer-events: none;
      }

      .previewThumb {
        width: 110px;
        aspect-ratio: 6 / 7;
        border-radius: 12px;
        position: relative;
        pointer-events: auto;

        :global(img) {
          border-radius: 12px;
        }
      }

      .previewCard {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: 66px 16px 16px;
        border-radius: 18px;
        background: linear-gradient(180deg, #0a0f2e 0%, #020716 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
      }

      .previewTopbar {
        position: absolute;
        top: 12px;
        left: 12px;
        right: 12px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        z-index: 20;
      }

      .previewTitleBlock {
        text-align: center;
        padding: 10px 4px;
        user-select: none;
        -webkit-user-select: none;
      }

      .previewName {
        color: #ffffff;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.2;
        margin: 0 0 4px;
      }

      .previewProvider {
        color: #98a2b3;
        font-size: 13px;
        font-weight: 500;
        margin: 0;
      }

      .previewPlayBtn {
        position: relative;
        overflow: hidden;
        display: flex;
        width: 100%;
        height: 35px;
        padding: 0 10px;
        justify-content: center;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
        border-radius: 6px;
        border: 1px solid #fc9984;
        background: radial-gradient(
          168.3% 138% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        color: #ffffff;
        text-align: center;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-size: 11px;
        font-weight: 600;
        line-height: 12px;
        cursor: pointer;
        transition:
          filter 0.15s ease,
          transform 0.1s ease;
      }

      .previewPlayBtn::before {
        content: '';
        position: absolute;
        top: -50%;
        left: -100%;
        width: 50%;
        height: 200%;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.5) 50%,
          transparent 100%
        );
        transform: skewX(-25deg);
        animation: previewShimmerSlide 1.5s ease-out infinite;
        pointer-events: none;
        z-index: 1;
      }

      .previewPlayBtn > * {
        position: relative;
        z-index: 2;
      }

      @keyframes previewShimmerSlide {
        0% {
          left: -100%;
        }
        100% {
          left: 150%;
        }
      }

      .previewPlayBtn:hover {
        filter: brightness(1.08);
      }

      .previewPlayBtn:active {
        transform: scale(0.98);
      }

      .previewPlayIcon {
        width: 11px;
        height: 13px;
        flex-shrink: 0;
        display: block;
      }

      /* Jogar com Bônus — mesma caixa do botão de saldo real, só a cor muda.
         Verde é o sinal combinado com o PRD pra carteira de bônus. */
      .previewBonusBtn {
        margin-top: 8px;
        border-color: #6ee7a8;
        background: radial-gradient(
          168.3% 138% at 36.76% -16.67%,
          #34d399 0%,
          #059669 100%
        );
      }

      .previewBonusIcon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        display: block;
      }

      .previewBonusBalance {
        color: #98a2b3;
        font-size: 11px;
        font-weight: 500;
        text-align: center;
        margin-top: 8px;
      }

      .previewBonusBalance strong {
        color: #34d399;
        font-weight: 700;
      }

      /* Regra de rollover — informativa, um degrau abaixo do saldo. */
      .previewBonusRollover {
        color: #667085;
        font-size: 10px;
        font-weight: 500;
        text-align: center;
        margin-top: 2px;
      }

      .previewChips {
        margin-top: 16px;
      }
    }

    .genericError {
      @apply flex flex-col items-center justify-center gap-2.5 text-error;
    }

    .btnCta {
      @apply flex justify-center items-center gap-1.5 py-2.5 px-6 font-bold text-base rounded-full;

      svg {
        @apply w-4 h-4 text-button-text;
      }
    }
  }

  &.isFullScreen {
    /* Sem canto arredondado nem borda no fullscreen: a barra de cima fecha
       arredondada por cima do jogo, e o rounded-t daqui deixava o fundo do
       site vazando entre os corners. */
    @apply w-full fixed top-0 left-0 z-[999] rounded-none border-0;
    /* Abre espaço pra games-bar (Bar.vue). A altura vem de :root pra não
       existirem dois valores soltos que saem de sincronia quando a barra muda. */
    height: calc(100% - var(--games-bar-h));
    margin-top: var(--games-bar-h);
  }

  .frameBg {
    @apply absolute top-0 left-0 z-0 w-full h-full bg-cover bg-top blur-md opacity-20 transform scale-125;
  }
}

.gameWrapper {
  @apply h-full md:mt-0;

  iframe {
    width: 100% !important;
    height: 100% !important;
    border: 0 !important;
  }
}
</style>
